import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, interval, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { VideoQualityProfile, VideoStats, VIDEO_QUALITY_PROFILES, RtpStreamStats, MAX_VIDEO_BITRATE } from './video.interface';

export { MAX_VIDEO_BITRATE } from './video.interface';

interface PreviousStatsData {
  outboundBytesSent: number;
  outboundTimestamp: number;
  inboundBytesReceived: number;
  inboundTimestamp: number;
}

@Injectable({
  providedIn: 'root'
})
export class VideoQualityService {
  private videoStatsMap = new Map<string, BehaviorSubject<VideoStats | null>>();
  private statsMonitoringMap = new Map<string, Subject<void>>();
  private previousStatsMap = new Map<string, PreviousStatsData>();

  constructor() {
    console.log('[VideoQualityService] Using automatic adaptive mode with 3 Mbps cap (1080p24 max)');
  }

  /**
   * Get quality profile - always returns 'auto' profile for maximum resolution
   * The browser's encoder handles adaptive scaling within the bitrate cap
   */
  getQualityProfile(): VideoQualityProfile {
    return VIDEO_QUALITY_PROFILES['auto'];
  }

  /**
   * Create video constraints for maximum resolution (1080p)
   * Browser encoder will scale down automatically based on bandwidth
   */
  createVideoConstraints(deviceId?: string): MediaTrackConstraints {
    const profile = VIDEO_QUALITY_PROFILES['auto'];
    const constraints: MediaTrackConstraints = {
      width: { ideal: profile.width },
      height: { ideal: profile.height },
      frameRate: { ideal: profile.maxFramerate },
      aspectRatio: { ideal: profile.aspectRatio }
    };

    if (deviceId && deviceId !== 'default') {
      constraints.deviceId = { exact: deviceId };
    }

    return constraints;
  }

  /**
   * Start monitoring video statistics for a call (for display purposes)
   */
  startStatsMonitoring(callId: string, peerConnection: RTCPeerConnection): void {
    // Clean up existing monitoring if any
    this.stopStatsMonitoring(callId);

    const statsSubject = new BehaviorSubject<VideoStats | null>(null);
    const stopSubject = new Subject<void>();

    this.videoStatsMap.set(callId, statsSubject);
    this.statsMonitoringMap.set(callId, stopSubject);
    this.previousStatsMap.set(callId, {
      outboundBytesSent: 0,
      outboundTimestamp: 0,
      inboundBytesReceived: 0,
      inboundTimestamp: 0
    });

    // Monitor stats every 1 second (for display purposes only)
    interval(1000)
      .pipe(takeUntil(stopSubject))
      .subscribe(async () => {
        try {
          const stats = await this.getVideoStatsFromPeerConnection(callId, peerConnection);
          if (stats) {
            statsSubject.next(stats);
          }
        } catch (error) {
          console.error('Error getting video stats:', error);
        }
      });
  }

  /**
   * Stop monitoring video statistics for a call
   */
  stopStatsMonitoring(callId: string): void {
    const stopSubject = this.statsMonitoringMap.get(callId);
    if (stopSubject) {
      stopSubject.next();
      stopSubject.complete();
      this.statsMonitoringMap.delete(callId);
    }

    const statsSubject = this.videoStatsMap.get(callId);
    if (statsSubject) {
      statsSubject.complete();
      this.videoStatsMap.delete(callId);
    }

    this.previousStatsMap.delete(callId);
  }

  /**
   * Get video statistics observable for a call
   */
  getVideoStats(callId: string): Observable<VideoStats | null> {
    const statsSubject = this.videoStatsMap.get(callId);
    if (!statsSubject) {
      return new BehaviorSubject<VideoStats | null>(null).asObservable();
    }
    return statsSubject.asObservable().pipe(filter(stats => stats !== null));
  }

  /**
   * Extract video statistics from RTCPeerConnection
   */
  private async getVideoStatsFromPeerConnection(callId: string, peerConnection: RTCPeerConnection): Promise<VideoStats | null> {
    try {
      const stats = await peerConnection.getStats();
      const previousStats = this.previousStatsMap.get(callId);
      const currentTimestamp = Date.now();

      const outbound: RtpStreamStats = {
        resolution: '',
        frameWidth: 0,
        frameHeight: 0,
        fps: 0,
        bitrate: 0,
        bytesTotal: 0,
        packetsTotal: 0,
        packetsLost: 0
      };

      const inbound: RtpStreamStats = {
        resolution: '',
        frameWidth: 0,
        frameHeight: 0,
        fps: 0,
        bitrate: 0,
        bytesTotal: 0,
        packetsTotal: 0,
        packetsLost: 0
      };

      let outboundTimestamp = 0;
      let inboundTimestamp = 0;
      let inboundAudioCodec: string | undefined;
      let outboundAudioCodec: string | undefined;

      // Collect codec IDs from RTP reports and codec reports during iteration
      let outboundVideoCodecId: string | undefined;
      let inboundVideoCodecId: string | undefined;
      let inboundAudioCodecId: string | undefined;
      let outboundAudioCodecId: string | undefined;
      const codecReports = new Map<string, { mimeType?: string }>();

      stats.forEach((report) => {
        // Collect codec reports for later resolution
        if (report.type === 'codec') {
          codecReports.set(report.id, report);
        }

        // Outbound video track stats
        if (report.type === 'outbound-rtp' && report.kind === 'video') {
          outboundTimestamp = report.timestamp;
          outboundVideoCodecId = report.codecId;

          if (report.bytesSent !== undefined) {
            outbound.bytesTotal = report.bytesSent;

            // Calculate bitrate from previous stats
            if (previousStats && previousStats.outboundBytesSent > 0 && previousStats.outboundTimestamp > 0) {
              const bytesDiff = report.bytesSent - previousStats.outboundBytesSent;
              const timeDiff = (report.timestamp - previousStats.outboundTimestamp) / 1000;
              if (timeDiff > 0) {
                outbound.bitrate = (bytesDiff * 8) / timeDiff;
              }
            }
          }

          if (report.packetsSent !== undefined) {
            outbound.packetsTotal = report.packetsSent;
          }

          if (report.frameWidth && report.frameHeight) {
            outbound.frameWidth = report.frameWidth;
            outbound.frameHeight = report.frameHeight;
            outbound.resolution = `${report.frameWidth}x${report.frameHeight}`;
          }

          if (report.framesPerSecond !== undefined) {
            outbound.fps = Math.round(report.framesPerSecond);
          }
        }

        // Outbound audio track stats (for audio codec)
        if (report.type === 'outbound-rtp' && report.kind === 'audio') {
          outboundAudioCodecId = report.codecId;
        }

        // Inbound video track stats
        if (report.type === 'inbound-rtp' && report.kind === 'video') {
          inboundTimestamp = report.timestamp;
          inboundVideoCodecId = report.codecId;

          if (report.bytesReceived !== undefined) {
            inbound.bytesTotal = report.bytesReceived;

            // Calculate bitrate from previous stats
            if (previousStats && previousStats.inboundBytesReceived > 0 && previousStats.inboundTimestamp > 0) {
              const bytesDiff = report.bytesReceived - previousStats.inboundBytesReceived;
              const timeDiff = (report.timestamp - previousStats.inboundTimestamp) / 1000;
              if (timeDiff > 0) {
                inbound.bitrate = (bytesDiff * 8) / timeDiff;
              }
            }
          }

          if (report.packetsReceived !== undefined) {
            inbound.packetsTotal = report.packetsReceived;
          }

          if (report.packetsLost !== undefined) {
            inbound.packetsLost = report.packetsLost;
          }

          if (report.jitter !== undefined) {
            inbound.jitter = report.jitter * 1000; // Convert to ms
          }

          if (report.frameWidth && report.frameHeight) {
            inbound.frameWidth = report.frameWidth;
            inbound.frameHeight = report.frameHeight;
            inbound.resolution = `${report.frameWidth}x${report.frameHeight}`;
          }

          if (report.framesPerSecond !== undefined) {
            inbound.fps = Math.round(report.framesPerSecond);
          }
        }

        // Inbound audio track stats (for audio codec)
        if (report.type === 'inbound-rtp' && report.kind === 'audio') {
          inboundAudioCodecId = report.codecId;
        }

        // Remote outbound stats (for outbound packet loss)
        if (report.type === 'remote-inbound-rtp' && report.kind === 'video') {
          if (report.packetsLost !== undefined) {
            outbound.packetsLost = report.packetsLost;
          }
          if (report.jitter !== undefined) {
            outbound.jitter = report.jitter * 1000; // Convert to ms
          }
        }
      });

      // Resolve codec names from collected codec reports
      if (outboundVideoCodecId) {
        const codecReport = codecReports.get(outboundVideoCodecId);
        if (codecReport?.mimeType) {
          outbound.codec = codecReport.mimeType.replace('video/', '');
        }
      }
      if (inboundVideoCodecId) {
        const codecReport = codecReports.get(inboundVideoCodecId);
        if (codecReport?.mimeType) {
          inbound.codec = codecReport.mimeType.replace('video/', '');
        }
      }
      if (inboundAudioCodecId) {
        const codecReport = codecReports.get(inboundAudioCodecId);
        if (codecReport?.mimeType) {
          inboundAudioCodec = codecReport.mimeType.replace('audio/', '');
        }
      }
      if (outboundAudioCodecId) {
        const codecReport = codecReports.get(outboundAudioCodecId);
        if (codecReport?.mimeType) {
          outboundAudioCodec = codecReport.mimeType.replace('audio/', '');
        }
      }

      // Update previous stats for next calculation
      if (previousStats) {
        previousStats.outboundBytesSent = outbound.bytesTotal;
        previousStats.outboundTimestamp = outboundTimestamp || currentTimestamp;
        previousStats.inboundBytesReceived = inbound.bytesTotal;
        previousStats.inboundTimestamp = inboundTimestamp || currentTimestamp;
      }

      const videoStats: VideoStats = {
        timestamp: currentTimestamp,
        inbound: inbound.bytesTotal > 0 ? inbound : undefined,
        outbound: outbound.bytesTotal > 0 ? outbound : undefined,
        inboundAudioCodec,
        outboundAudioCodec
      };

      return videoStats;
    } catch (error) {
      console.error('Error extracting video stats:', error);
      return null;
    }
  }

  /**
   * Apply the 3 Mbit/s bitrate limit to RTCPeerConnection video sender.
   * This enforces the bandwidth cap while letting the encoder handle resolution scaling.
   */
  async applyBitrateLimit(sender: RTCRtpSender): Promise<void> {
    const parameters = sender.getParameters();

    if (!parameters.encodings || parameters.encodings.length === 0) {
      parameters.encodings = [{}];
    }

    parameters.encodings[0].maxBitrate = MAX_VIDEO_BITRATE;

    try {
      await sender.setParameters(parameters);
      console.log(`[VideoQualityService] Applied bitrate limit: ${MAX_VIDEO_BITRATE / 1000} kbps`);
    } catch (error) {
      console.error('[VideoQualityService] Error applying bitrate limit:', error);
    }
  }
}
